// Транспорт раздела «Документы по сделке»: счета, договоры, отчёты. ТОЛЬКО ЧТЕНИЕ.
//
// ЗАЧЕМ ОТДЕЛЬНЫЙ ФАЙЛ, ЕСЛИ ЕСТЬ api/hooks.ts
// Соседние экраны /smart/* читают ЖИВОЙ Bitrix (`crm.item.list` через BFF), по одному
// типу на экран. Этот раздел читает ЗЕРКАЛО и все четыре типа сразу. Источник другой,
// форма ответа другая, оговорки другие — складывать их хуки в один файл значило бы
// однажды переиспользовать «покажи документы» и не заметить, что вместо зеркала
// спрашиваешь портал, которого через месяц не будет.
//
// Записи здесь нет и появиться не должно: на полях этих типов в Bitrix висят
// бизнес-процессы, которые ставят задачи живым людям.

import { useQuery } from '@tanstack/react-query'

import { CoreUnsupported, Unauthorized } from './client'

const БАЗА = '/api/docs'

/** Сколько строк на странице. То же число, что у соседних разделов зеркала. */
export const РАЗМЕР_СТРАНИЦЫ = 50

/**
 * Тот же разбор ответа, что в api/client.ts и api/coordProjects.ts. Продублирован
 * намеренно: общего «api/base.ts» в проекте нет, и вводить его ради двадцати строк
 * значило бы поставить всем разделам общую точку отказа.
 */
async function запрос<T>(путь: string): Promise<T> {
  const res = await fetch(БАЗА + путь, { credentials: 'same-origin' })
  if (res.status === 401) throw new Unauthorized()
  const текст = await res.text()
  const данные: unknown = текст ? JSON.parse(текст) : {}
  if (!res.ok) {
    const rec = (данные || {}) as Record<string, unknown>
    if (res.status === 403) {
      throw new Error('раздел «Документы по сделке» вам не открыт')
    }
    // 400 от ядра здесь — это неверный фильтр. Пустая таблица читалась бы как
    // «документов нет», а по счетам за таким ответом идут звонить в бухгалтерию.
    if (res.status === 400) throw new CoreUnsupported(String(rec.why ?? rec.error ?? ''))
    const msg = (rec.error as string) || (rec.detail as string) || `HTTP ${res.status}`
    throw new Error(msg)
  }
  return данные as T
}

// ─────────────── формы ответов ядра (см. docs_api.py) ───────────────

export interface Список<T> {
  rows: T[]
  total: number
  next: number | null
  limit: number
  start: number
  cutoff: string
}

/**
 * Строка реестра документов.
 *
 * `amount` равен `null` У ДВУХ РАЗНЫХ ПРИЧИН, и различать их обязан экран, а не глаз:
 * у договоров суммы нет вовсе (`has_money` типа = false), у отчётов по сделкам она
 * есть, но заполнена у трёх из десяти. Ноль при этом — настоящая сумма: документов,
 * выставленных ровно на ноль, три сотни, и прочерком их прятать нельзя.
 *
 * `deal_gone` — сделка указана, но в зеркале её нет. Это не «сделки не было»: синк
 * сделок отстаёт на минуты, и строка честнее прочерка.
 */
export interface ДокументСтрока {
  entity_type_id: number
  bitrix_id: number
  category_id: number | null
  stage_id: string | null
  title: string | null
  doc_number: string | null
  doc_date: string | null
  due_date: string | null
  paid_date: string | null
  amount: number | null
  currency: string | null
  company_id: number | null
  company_title: string | null
  parent_deal_id: number | null
  deal_title: string | null
  deal_gone: boolean | null
  assigned_by_id: number | null
  assigned_name: string | null
  created_time: string | null
  updated_time: string | null
}

export interface ИтогиСписка {
  docs: number | null
  with_amount: number | null
  no_amount: number | null
  amount_sum: number | null
  zero_amount: number | null
  deals: number | null
  companies: number | null
  no_deal: number | null
  types: number | null
}

export interface СтадияСправочника {
  stage_id: string
  name: string | null
  sort: number | null
  semantics: string | null
  color: string | null
}

export interface ТипДокумента {
  kind: string
  type: number
  title: string
  what: string
  side: string
  has_money: boolean
  docs: number
  with_amount: number
  no_amount: number
  zero_amount: number
  amount_sum: number | null
  first_at: string | null
  last_at: string | null
  stages: СтадияСправочника[]
}

export interface Свежесть {
  entity: string
  last_sync_at: string | null
  last_count: number | null
  age_min: number | null
}

export interface ОхватИстории {
  entity_type_id: number
  rows: number | null
  owners: number | null
  first_at: string | null
  last_at: string | null
}

export interface СправочникиДокументов {
  source: string
  cutoff: string
  types: ТипДокумента[]
  stage_names: Record<string, string>
  counts: Record<string, number | string | null>
  freshness: Свежесть[]
  stage_history_coverage: ОхватИстории[]
  sort_list: string[]
  hidden_field_types: string[]
  data_notes: Record<string, string>
  caveats: string[]
}

export interface СтадияСводки {
  stage_id: string | null
  stage_name: string | null
  sort: number | null
  docs: number
  with_amount: number
  no_amount: number
  amount_sum: number | null
  zero_amount: number
  deals: number
  first_at: string | null
  last_at: string | null
}

export interface ГруппаСводки {
  kind: string
  type: number
  title: string
  what: string
  has_money: boolean
  docs: number
  with_amount: number
  no_amount: number
  zero_amount: number
  amount_sum: number | null
  stages: СтадияСводки[]
}

export interface Сводка {
  cutoff: string
  groups: ГруппаСводки[]
  stage_names: Record<string, string>
  totals: {
    docs: number
    with_amount: number
    no_amount: number
    amount_sum: number
  }
}

/** Поле карточки. Значения полей-файлов и полей-ссылок сюда не попадают вовсе. */
export interface ПолеКарточки {
  code: string
  title: string
  field_type: string | null
  fill_pct: number | null
  value: unknown
}

/**
 * Что вырезано из карточки и почему.
 *
 * Названия полей здесь есть, значений и адресов — нет, и это ровно та граница, которую
 * раздел держит: молча вырезать поле нельзя (человек решит, что зеркало потеряло скан
 * и пойдёт проверять синк), показать содержимое — тоже нельзя.
 */
export interface СкрытыеПоля {
  count: number
  filled_here: number
  field_types: string[]
  names: string[]
  why: string
}

export interface ЗаписьЛенты {
  bitrix_id: number
  author_id: number | null
  created: string | null
  comment_text: string | null
  has_files: boolean
  files_count: number
  kind: string | null
  author_name: string | null
}

export interface ПереходСтадии {
  created_time: string | null
  stage_id: string | null
  stage_semantic_id: string | null
  category_id: number | null
}

export interface КарточкаДокумента {
  kind: string
  type: number
  title: string
  what: string
  side: string
  has_money: boolean
  doc: ДокументСтрока & {
    contact_id: number | null
    synced_at: string | null
    deal_stage_id: string | null
    deal_closed: boolean | null
    deal_amount: number | null
    deal_currency: string | null
  }
  fields: ПолеКарточки[]
  hidden_fields: СкрытыеПоля
  timeline: ЗаписьЛенты[]
  timeline_total: number
  stage_history: ПереходСтадии[]
  /** Границы того, что синк успел прочитать ПО ЭТОМУ ТИПУ. Пустая история без них
   *  читается как «стадию не меняли», а это другое утверждение. */
  stage_history_coverage: {
    rows: number | null
    owners: number | null
    first_at: string | null
    last_at: string | null
  }
  cutoff: string
}

export interface ГруппаСделки {
  kind: string
  type: number
  title: string
  what: string
  has_money: boolean
  docs: number
  with_amount: number
  no_amount: number
  amount_sum: number | null
  rows: ДокументСтрока[]
}

export interface ДокументыСделки {
  deal: {
    bitrix_id: number
    title: string | null
    stage_id: string | null
    closed: boolean | null
    opportunity: number | null
    currency: string | null
    company_id: number | null
    company_title: string | null
    assigned_by_id: number | null
    assigned_name: string | null
  } | null
  deal_in_mirror: boolean
  groups: ГруппаСделки[]
  products: {
    positions: number | null
    amount_sum: number | null
    no_price: number | null
  }
  docs_total: number
  cutoff: string
}

// ─────────────────────────────── хуки ───────────────────────────────

function строкаЗапроса(ф: Record<string, unknown>): string {
  const p = new URLSearchParams()
  for (const [k, v] of Object.entries(ф)) {
    if (v === undefined || v === null || v === '' || v === false) continue
    p.set(k, v === true ? '1' : String(v))
  }
  const s = p.toString()
  return s ? '?' + s : ''
}

const ОБЩЕЕ = { staleTime: 60_000, retry: 1 } as const

export function useСправочникиДокументов() {
  return useQuery<СправочникиДокументов>({
    queryKey: ['docs', 'refs'],
    queryFn: () => запрос<СправочникиДокументов>('/refs'),
    ...ОБЩЕЕ,
  })
}

/**
 * Страница списка. Итоги здесь НЕ спрашиваются намеренно.
 *
 * Сумма отчётов лежит внутри jsonb, который база распаковывает построчно: страница без
 * итогов отвечает за 152 мс, с итогами — за 850 мс (замер по боевому зеркалу
 * 29.07.2026, 41 994 документа). От номера страницы итоги не зависят, поэтому платить
 * эти секунды при каждом перелистывании не за что — их спрашивает отдельный хук,
 * в ключ которого номер страницы не входит.
 */
export function useДокументы(ф: Record<string, unknown>) {
  return useQuery<Список<ДокументСтрока>>({
    queryKey: ['docs', 'list', ф],
    queryFn: () => запрос('/list' + строкаЗапроса(ф)),
    ...ОБЩЕЕ,
  })
}

/**
 * Итоги по ВСЕЙ выборке — отдельным запросом, ключ без пагинации.
 *
 * Считать их по видимым пятидесяти строкам было нельзя: «на 8,9 млрд» под таблицей из
 * сорока двух тысяч документов — это про всю выборку, а не про то, что поместилось
 * на экран.
 */
export function useИтогиДокументов(ф: Record<string, unknown>) {
  return useQuery<Список<ДокументСтрока> & { totals: ИтогиСписка }>({
    queryKey: ['docs', 'totals', ф],
    queryFn: () => запрос('/list' + строкаЗапроса({ ...ф, limit: 1, totals: 1 })),
    ...ОБЩЕЕ,
  })
}

/**
 * Сводка по стадиям. `enabled` — не украшение: сводка ходит в те же денежные поля
 * внутри jsonb и стоит около 650 мс, а вкладку «Сводка» открывают не всегда. Считать
 * её вместе с реестром значило бы платить эти миллисекунды каждому, кто пришёл
 * посмотреть список.
 */
export function useСводкаДокументов(ф: Record<string, unknown>, enabled = true) {
  return useQuery<Сводка>({
    queryKey: ['docs', 'summary', ф],
    queryFn: () => запрос('/summary' + строкаЗапроса(ф)),
    enabled,
    ...ОБЩЕЕ,
  })
}

/** Одна карточка. Пока тип и номер не выбраны — не ходим вовсе, а не спрашиваем «ничего». */
export function useКарточкаДокумента(тип?: string, ид?: number) {
  return useQuery<КарточкаДокумента>({
    queryKey: ['docs', 'item', тип, ид],
    queryFn: () => запрос(`/item?type=${encodeURIComponent(тип ?? '')}&id=${ид}`),
    enabled: !!тип && !!ид,
    ...ОБЩЕЕ,
  })
}

export function useДокументыСделки(сделка?: number) {
  return useQuery<ДокументыСделки>({
    queryKey: ['docs', 'deal', сделка],
    queryFn: () => запрос(`/deal?deal=${сделка}`),
    enabled: !!сделка,
    ...ОБЩЕЕ,
  })
}

/**
 * Оговорки раздела и порядок их чтения. Тексты и числа считает ядро — здесь только
 * ключи: свой текст неизбежно оброс бы вбитым числом, а оно однажды разойдётся с тем,
 * что человек видит рядом.
 */
export const ОГОВОРКИ_ДОКУМЕНТОВ = [
  'cutoff', 'no_write', 'no_money_types', 'zero_amount',
  'no_files', 'no_deal', 'stage_history', 'source',
]

/** Как называется сущность зеркала в подписи «обновлено N мин назад». */
export const ИМЯ_СУЩНОСТИ: Record<string, string> = {
  'items:31': 'счета клиенту',
  'items:1160': 'счета на оплату',
  'items:153': 'договоры',
  'items:176': 'отчёты по сделкам',
}

/** Самая отставшая из сущностей раздела. Свежесть показываем по ней, а не по средней. */
export function самаяСтарая(список?: Свежесть[]): Свежесть | undefined {
  const свои = (список ?? []).filter((с) => с.entity in ИМЯ_СУЩНОСТИ)
  if (!свои.length) return undefined
  return свои.reduce((а, б) => ((б.age_min ?? 0) > (а.age_min ?? 0) ? б : а))
}
